import React, { FC, useCallback, useEffect, useState } from 'react';
import { RequiredNameProps } from 'common/types/utility';
import {
  TextField,
  ITextFieldProps,
  Stack,
  Spinner,
  useTheme,
  Link,
  Text,
  TooltipHost,
  Icon,
  IRenderFunction,
} from '@fluentui/react';
import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import { LookUpCallout } from './Lookup';
import { useCommonStyles } from 'common/styles';
import { useStyles } from './index.styles';
import { GetEntityDocument_entityDocument_documentFileDistributionsByDocumentFileId_nodes } from 'documents/documentAssignment/folder/view/__generated__/GetEntityDocument';
import { RefCode1, RefCode1Variables } from './__generated__/RefCode1';
import { useController } from 'react-hook-form';
import { ReferenceCodeOptionsType } from '../interface';

import REFERENCE_CODES from './RefCode1.graphql';
type AutoCompleteFFTextFieldProps = RequiredNameProps<ITextFieldProps> & {
  width: number;
  index: number;
  invoiceDistributions:
    | GetEntityDocument_entityDocument_documentFileDistributionsByDocumentFileId_nodes[]
    | undefined;
};

export const AutoCompleteFF1: FC<AutoCompleteFFTextFieldProps> = ({
  width,
  index,
  invoiceDistributions,
  ...props
}) => {
  const theme = useTheme();
  const styles = useStyles();

  const {
    field,
    fieldState: { error },
  } = useController({
    name: props.name,
  });

  const { onChange, value } = { ...field };
  const [calloutVisible, setCalloutVisible] = useState(false);
  const [referenceCode, setReferenceCode] = useState<string | null>();
  const [referenceCodeOptions, setReferenceCodeOptions] = useState<
    ReferenceCodeOptionsType[]
  >([]);

  const [fetchReferenceCodes, { data, loading }] = useLazyQuery<
    RefCode1,
    RefCode1Variables
  >(REFERENCE_CODES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    onCompleted: (data) => {
      if (data.reference1Codes?.nodes.length) {
        const position = data.reference1Codes.nodes.findIndex(
          (item) => item.referenceCode.toUpperCase() === value?.toUpperCase()
        );
        if (position === -1) {
          // helpers.setError('Select list');
        } else clearFormikFields();
      } else clearFormikFields();
    },
  });
  const { id } = { ...props };

  useEffect(() => {
    if (invoiceDistributions && invoiceDistributions.length > 0) {
      setReferenceCode(invoiceDistributions[index]?._ff1Name!);
    }
  }, [invoiceDistributions, index]);
  useEffect(() => {
    if (data) {
      const refOptions: ReferenceCodeOptionsType[] =
        data?.reference1Codes?.nodes.map((item, index) => ({
          key: index,
          text: item.name,
          referenceCode: item.referenceCode,
        })) || [];
      setReferenceCodeOptions(refOptions);
    }
  }, [data]);

  const callbackLookUp = useCallback(
    debounce((value, queryCalled) => {
      fetchReferenceCodes({
        variables: {
          Ref1CodeFilter: {
            referenceCode: {
              startsWithInsensitive: value,
            },
          },
        },
      });
      queryCalled();
    }, 350),
    [value]
  );

  const clearFormikFields = () => {
    // helpers.setTouched(false);
    // helpers.setError(null);
    setReferenceCode('');
  };

  const commonStyles = useCommonStyles();

  const onRenderSuffix: IRenderFunction<ITextFieldProps> = (props) => {
    if (loading) return <Spinner />;
    else
      return (
        <button
          className={styles.customDropdownIcon}
          onClick={() => {
            callbackLookUp('', () => {
              if (!calloutVisible) setCalloutVisible(true);
            });
          }}
          tabIndex={-1}
        >
          <Icon iconName="ChevronDown" />
        </button>
      );
  };

  const textfieldProps: ITextFieldProps | undefined = { onRenderSuffix };

  return (
    <Stack style={{ marginTop: error?.message ? 0 : 3 }}>
      <TooltipHost content={referenceCode!}>
        <Stack horizontal horizontalAlign="space-between" style={{}}>
          <TextField
            {...props}
            autoComplete={'off'}
            errorMessage={error?.message}
            value={value || ''}
            onChange={(_event, value) => {
              if (value) {
                onChange(value);
                callbackLookUp(value, () => {
                  if (!calloutVisible) setCalloutVisible(true);
                });
              } else {
                onChange(null);
                setCalloutVisible(false);
              }
            }}
            underlined
            {...textfieldProps}
            styles={{
              suffix: { backgroundColor: theme.palette.white },
              fieldGroup: { width: width },
            }}
          />
        </Stack>
        <Text style={{ color: theme.palette.white }}>{error?.message}</Text>
      </TooltipHost>

      {calloutVisible && !loading && referenceCodeOptions.length! > 0 && (
        <LookUpCallout
          lookUpCalloutWidth={300}
          onDismiss={() => setCalloutVisible(false)}
          targetID={`#${id!}`}
        >
          {referenceCodeOptions.map((data, index) => {
            return (
              <Link
                onClick={() => {
                  onChange(data.referenceCode);
                  setReferenceCode(data.text);
                  setCalloutVisible(false);
                }}
                className={styles.resultItem}
                key={index.toString()}
              >
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                  <Text className={commonStyles.colorThemePrimary}>
                    {data.referenceCode}
                  </Text>
                  <Text style={{ width: 220 }} nowrap>
                    {data.text}
                  </Text>
                </Stack>
              </Link>
            );
          })}
        </LookUpCallout>
      )}
    </Stack>
  );
};
